import { defineMessages } from "react-intl";

export const MESSAGES = defineMessages({
  pageTitle: {
    id: "funding-application: accounts page title",
    defaultMessage: "Add all your bank accounts",
  },
  pageSubtitle: {
    id: "funding-application: accounts page subtitle",
    defaultMessage: "Include current accounts, credit cards, and any overdraft or credit line.",
  },
  addAccountInstantly: {
    id: "funding-application: add account instantly button",
    defaultMessage: "Add connection instantly",
  },
  addStatementsManually: {
    id: "funding-application: add statements manually button",
    defaultMessage: "Add or drop statements",
  },
  addStatementsMobile: {
    id: "funding-application: add statements button mobile",
    defaultMessage: "Add statements",
  },
  emptyStateConnectionCardTitle: {
    id: "funding-application: accounts page empty state connection card title",
    defaultMessage: "Connect your bank",
  },
  emptyStateConnectionCardSubtitle: {
    id: "funding-application: accounts page empty state connection card subtitle",
    defaultMessage: "Instant verification, usually under an hour.",
  },
  emptyStateRecommendedBadge: {
    id: "funding-application: accounts page empty state recommended badge",
    defaultMessage: "Recommended",
  },
  emptyStateRequiredBadge: {
    id: "funding-application: accounts page empty state required badge",
    defaultMessage: "Required",
  },
  emptyStateUploadCardTitle: {
    id: "funding-application: accounts page empty state upload card title",
    defaultMessage: "Upload statements",
  },
  emptyStateUploadCardSubtitle: {
    id: "funding-application: accounts page empty state upload card subtitle",
    defaultMessage: "Can take days, may require follow-up.",
  },
  uploadAvailableAfterBankConnection: {
    id: "funding-application: accounts page upload available after bank connection",
    defaultMessage: "Available after connecting your bank.",
  },
  ukBankingSatisfiedDisclosure: {
    id: "funding-application: accounts page uk banking satisfied disclosure",
    defaultMessage:
      "By clicking {nextStep} to continue, you confirm your details are correct. We may share your personal information with fraud prevention agencies, and if fraud is detected, you may be refused services, financing, or employment. Read our <privacyLink>Privacy Notice</privacyLink>.",
  },
  timeInBusinessBanner: {
    id: "funding-application: accounts page time in business explainer",
    defaultMessage:
      "Based on your business model, you'll need at least <b>{months} months</b> of trading history to qualify for funding. Connect your bank now and we'll let you know when you're eligible.",
  },
  floatingFilesListTitle: {
    id: "funding-application: accounts page floating files list title",
    defaultMessage: "Processing files",
  },
  floatingFilesListProgress: {
    id: "funding-application: accounts page floating files list progress",
    defaultMessage: "{done}/{total}",
  },
  floatingFilesListUploadingLabel: {
    id: "funding-application: accounts page floating files list uploading label",
    defaultMessage: "Uploading",
  },
  uploadFailedMessage: {
    id: "funding-application: accounts page upload failed message",
    defaultMessage: "Upload failed. Try again.",
  },
  cancelAction: {
    id: "funding-application: accounts page cancel action",
    defaultMessage: "Cancel",
  },
  retryAction: {
    id: "funding-application: accounts page retry action",
    defaultMessage: "Retry",
  },
  retryAllAction: {
    id: "funding-application: accounts page retry all action",
    defaultMessage: "Retry all",
  },
  doneAction: {
    id: "funding-application: accounts page done action",
    defaultMessage: "Done",
  },
  connectedBadge: {
    id: "funding-application: accounts page connected badge",
    defaultMessage: "Connected",
  },
  readyBadge: {
    id: "funding-application: accounts page ready badge",
    defaultMessage: "Ready",
  },
  manageAccountAction: {
    id: "funding-application: accounts page manage account action",
    defaultMessage: "Manage",
  },
  bankAccountsSectionTitle: {
    id: "funding-application: accounts page bank accounts section title",
    defaultMessage: "Bank accounts",
  },
  connectedWithProvider: {
    id: "funding-application: accounts page connected with provider subtitle",
    defaultMessage: "Connected with {provider} ({currency})",
  },
  connectedWithProviderNoCurrency: {
    id: "funding-application: accounts page connected with provider no currency subtitle",
    defaultMessage: "Connected with {provider}",
  },
  addedWithStatements: {
    id: "funding-application: accounts page added with statements subtitle",
    defaultMessage: "Added with statements ({currency})",
  },
  addedWithStatementsNoCurrency: {
    id: "funding-application: accounts page added with statements no currency subtitle",
    defaultMessage: "Added with statements",
  },
  connectedWithProviderAndStatements: {
    id: "funding-application: accounts page connected with provider and statements subtitle",
    defaultMessage: "Connected with {provider} and statements ({currency})",
  },
  connectedWithProviderAndStatementsNoCurrency: {
    id: "funding-application: accounts page connected with provider and statements no currency subtitle",
    defaultMessage: "Connected with {provider} and statements",
  },
  businessAccountFallback: {
    id: "funding-application: accounts page business account fallback label",
    defaultMessage: "Business account",
  },
  requiredFromYouHeading: {
    id: "funding-application: accounts page required from you heading",
    defaultMessage: "Required from you",
  },
  requiredFromYouBodyText: {
    id: "funding-application: accounts page required from you body text",
    defaultMessage:
      "We need continuous, up-to-date banking data from {startDate} onwards for each account linked to your business. If you're uploading statements, they must be PDF or CSV files under 50MB. {viewMore}",
  },
  viewMore: {
    id: "funding-application: accounts page view more link",
    defaultMessage: "View more.",
  },
  requirementsModalTitle: {
    id: "funding-application: accounts page requirements modal title",
    defaultMessage: "Required from you",
  },
  requirementsModalBody: {
    id: "funding-application: accounts page requirements modal body",
    defaultMessage:
      "We require any account that carries revenue, expenses, or debt. Include current accounts, credit cards and any overdraft or credit line. Add every account now, or we'll follow up later and slow down your application.",
  },
  requirementsModalListHeading: {
    id: "funding-application: accounts page requirements modal list heading",
    defaultMessage: "We'll ask for more information if the data has:",
  },
  requirementsModalRuleGaps: {
    id: "funding-application: accounts page requirements modal rule gaps",
    defaultMessage: "Gaps longer than 14 days",
  },
  requirementsModalRuleNoTransactions: {
    id: "funding-application: accounts page requirements modal rule no transactions",
    defaultMessage: "No transactions within the last 14 days",
  },
  requirementsModalFootnote: {
    id: "funding-application: accounts page requirements modal footnote",
    defaultMessage:
      "If a given account has had a genuine quiet period, is new, or was recently closed, you'll get a chance to explain later.",
  },
  connectModalTitle: {
    id: "funding-application: accounts page connect modal title",
    defaultMessage: "Connect your bank",
  },
  connectModalContinue: {
    id: "funding-application: accounts page connect modal continue",
    defaultMessage: "Continue",
  },
  bankLocationLabel: {
    id: "funding-application: accounts page bank location label",
    defaultMessage: "Bank location",
  },
  accountDetailsTypeAndCurrency: {
    id: "funding-application: accounts page account details type and currency",
    defaultMessage: "{accountType} account ({currency})",
  },
  accountDetailsType: {
    id: "funding-application: accounts page account details type",
    defaultMessage: "{accountType} account",
  },
  accountDetailsBodyText: {
    id: "funding-application: accounts page account details body text",
    defaultMessage: "Your account is connected. We'll process this data as part of your application.",
  },
  sourcesSectionTitle: {
    id: "funding-application: accounts page sources section title",
    defaultMessage: "Sources",
  },
  sourcesAddStatementsButton: {
    id: "funding-application: accounts page sources add statements button",
    defaultMessage: "Add or drop statements",
  },
  reassignedStatementSubtitle: {
    id: "funding-application: accounts page reassigned statement subtitle",
    defaultMessage: "This statement matches {institutionLabel} instead.",
  },
  aggregateCoverageConnected: {
    id: "funding-application: accounts page aggregate coverage connected",
    defaultMessage:
      "{count, plural, =1 {Your account is} other {Your accounts are}} connected. Add any other accounts linked to your business. We may follow up if additional information is needed.",
  },
  aggregateCoverageComplete: {
    id: "funding-application: accounts page aggregate coverage complete",
    defaultMessage: "You're all set. Add any other accounts linked to your business.",
  },
  viewRequirements: {
    id: "funding-application: accounts page view requirements link",
    defaultMessage: "View requirements.",
  },
  statementCoverageDates: {
    id: "funding-application: accounts page statement coverage dates",
    defaultMessage: "{startDate} to {endDate}",
  },
  statementCoverageComplete: {
    id: "funding-application: accounts page statement coverage complete",
    defaultMessage:
      "Your account is connected, you're all set for now. Add any other account linked to your business and continue your application.",
  },
  statementCoverageNone: {
    id: "funding-application: accounts page statement coverage none",
    defaultMessage: "We're missing transactions for this account. Connect or upload statements to continue.",
  },
  statementCoverageLeadingGap: {
    id: "funding-application: accounts page statement coverage leading gap",
    defaultMessage: "We haven't received any transactions before {date}. Connect or upload statements to continue.",
  },
  statementCoverageTrailingGap: {
    id: "funding-application: accounts page statement coverage trailing gap",
    defaultMessage: "We haven't received a transaction since {date}. Connect or upload statements to continue.",
  },
  statementCoverageCurrentMonthGap: {
    id: "funding-application: accounts page statement coverage current month gap",
    defaultMessage:
      "We haven't received a transaction since {date}. Connect, provide a mid-month statement, or export a CSV from your bank for the current month to continue.",
  },
  statementCoverageMiddleGap: {
    id: "funding-application: accounts page statement coverage middle gap",
    defaultMessage:
      "We haven't received any transactions for the period between {startDate} and {endDate}. Connect or upload statements to continue.",
  },
  statementCoverageLeadingAndTrailingGap: {
    id: "funding-application: accounts page statement coverage leading and trailing gap",
    defaultMessage:
      "We haven't received any transactions before {leadingDate} or since {trailingDate}. Connect or upload statements to continue.",
  },
  statementCoverageFragmentedGapsIntro: {
    id: "funding-application: accounts page statement coverage fragmented gaps intro",
    defaultMessage:
      "We haven't received any transactions for the following {count, plural, one {period} other {periods}}:",
  },
  statementCoverageFragmentedGapsOutro: {
    id: "funding-application: accounts page statement coverage fragmented gaps outro",
    defaultMessage: "Connect or upload statements to continue.",
  },
  extractionProcessingText: {
    id: "funding-application: accounts page extraction processing text",
    defaultMessage: "Reading statement details...",
  },
  calculatingCoverageText: {
    id: "funding-application: accounts page calculating coverage text",
    defaultMessage: "Calculating coverage...",
  },
  uploadInProgressLabel: {
    id: "funding-application: accounts page upload in progress label",
    defaultMessage: "Uploading...",
  },
  uploadInProgressPercentage: {
    id: "funding-application: accounts page upload in progress percentage",
    defaultMessage: "{progress}%",
  },
  fileRejectedMessage: {
    id: "funding-application: accounts page file rejected message",
    defaultMessage: "File type or size not supported.",
  },
  // temporary copy pending confirmed design for the extraction failure state
  extractionFailedText: {
    id: "funding-application: accounts page extraction failed text",
    defaultMessage: "We could not read this statement. Try uploading it again.",
  },
  otherStatementsUploadedLabel: {
    id: "funding-application: accounts page other statements uploaded label",
    defaultMessage: "Other statements uploaded",
  },
  statementsUploadedCountLabel: {
    id: "funding-application: accounts page statements uploaded count label",
    defaultMessage: "{count, plural, one {# statement} other {# statements}} uploaded",
  },
  otherStatementsUploadedSubtitle: {
    id: "funding-application: accounts page other statements uploaded subtitle",
    defaultMessage: "We'll review these after you submit your application",
  },
  viewAction: {
    id: "funding-application: accounts page view action",
    defaultMessage: "View",
  },
  reassignedBadge: {
    id: "funding-application: accounts page reassigned badge",
    defaultMessage: "Reassigned",
  },
  reassignedSubtitle: {
    id: "funding-application: accounts page reassigned subtitle",
    defaultMessage: "This connection was reassigned to {institutionLabel}",
  },
  toReviewBadge: {
    id: "funding-application: accounts page to review badge",
    defaultMessage: "To review",
  },
  unmatchedStatementsPanelBodyText: {
    id: "funding-application: accounts page unmatched statements panel body text",
    defaultMessage:
      "We'll review these alongside your application once you submit. We'll email you if we need any more information.",
  },
  confirmAccountsPanelTitle: {
    id: "funding-application: accounts page confirm accounts panel title",
    defaultMessage: "Select accounts",
  },
  confirmAccountsPanelPrimaryDescription: {
    id: "funding-application: accounts page confirm accounts panel description",
    defaultMessage: "Connect all accounts associated with your business for the most accurate offer.",
  },
  confirmAccountsSaveButton: {
    id: "funding-application: accounts page confirm accounts save button",
    defaultMessage: "Save accounts",
  },
  moreOptionsAccessibilityLabel: {
    id: "funding-application: accounts page more options accessibility label",
    defaultMessage: "More options",
  },
  reassignMenuAction: {
    id: "funding-application: accounts page reassign menu action",
    defaultMessage: "Reassign",
  },
  removeMenuAction: {
    id: "funding-application: accounts page remove menu action",
    defaultMessage: "Remove",
  },
  removeStatementLastSourceTitle: {
    id: "funding-application: accounts page remove statement last source title",
    defaultMessage: "Delete {accountLabel}?",
  },
  removeStatementLastSourceBodyText: {
    id: "funding-application: accounts page remove statement last source body text",
    defaultMessage:
      "This is the last source on this account. Removing it will delete {accountLabel} from your bank accounts.",
  },
  removeStatementDeleteAccountAction: {
    id: "funding-application: accounts page remove statement delete account action",
    defaultMessage: "Delete account",
  },
  removeStatementKeepAccountAction: {
    id: "funding-application: accounts page remove statement keep account action",
    defaultMessage: "Keep account",
  },
  assignStatementTitle: {
    id: "funding-application: accounts page assign statement title",
    defaultMessage: "Assign {fileName}",
  },
  assignStatementBodyText: {
    id: "funding-application: accounts page assign statement body text",
    defaultMessage: "Choose which account this statement belongs to.",
  },
  assignStatementApplyAction: {
    id: "funding-application: accounts page assign statement apply action",
    defaultMessage: "Apply",
  },
  assignStatementPlaceholder: {
    id: "funding-application: accounts page assign statement placeholder",
    defaultMessage: "Select an account",
  },
  keepHereOption: {
    id: "funding-application: accounts page keep here option",
    defaultMessage: "Keep here",
  },
  assignStatementSuccessToast: {
    id: "funding-application: accounts page assign statement success toast",
    defaultMessage: "{fileName} assigned to {accountName}.",
  },
  assignStatementUndoAction: {
    id: "funding-application: accounts page assign statement undo action",
    defaultMessage: "Undo",
  },
  addNewAccountOption: {
    id: "funding-application: accounts page add a new account option",
    defaultMessage: "Add a new account",
  },
  addNewAccountConfirmAction: {
    id: "funding-application: accounts page add new account confirm action",
    defaultMessage: "Add account",
  },
  addNewAccountBankNameLabel: {
    id: "funding-application: accounts page add new account bank name label",
    defaultMessage: "Bank name",
  },
  addNewAccountNameLabel: {
    id: "funding-application: accounts page add new account name label",
    defaultMessage: "Account name",
  },
  addNewAccountTypeLabel: {
    id: "funding-application: accounts page add new account type label",
    defaultMessage: "Account type",
  },
  addNewAccountCurrencyLabel: {
    id: "funding-application: accounts page add new account currency label",
    defaultMessage: "Currency",
  },
  addNewAccountMaskLabel: {
    id: "funding-application: accounts page add new account mask label",
    defaultMessage: "Account number (last 4 digits)",
  },
  accountTypeChecking: {
    id: "funding-application: accounts page account type checking",
    defaultMessage: "Checking",
  },
  accountTypeSavings: {
    id: "funding-application: accounts page account type savings",
    defaultMessage: "Savings",
  },
  accountTypeCreditCard: {
    id: "funding-application: accounts page account type credit card",
    defaultMessage: "Credit card",
  },
  accountTypeOther: {
    id: "funding-application: accounts page account type other",
    defaultMessage: "Other",
  },
  groupDetailsPanelConfirmButton: {
    id: "funding-application: accounts page group details panel confirm button",
    defaultMessage: "Confirm",
  },
  notConnectedBadge: {
    id: "funding-application: accounts page not connected badge",
    defaultMessage: "Not connected",
  },
  reconnectAction: {
    id: "funding-application: accounts page reconnect action",
    defaultMessage: "Reconnect",
  },
  disconnectedBodyText: {
    id: "funding-application: accounts page disconnected body text",
    defaultMessage:
      "Your account was disconnected. Reconnect or if this issue persists, upload statements to continue.",
  },
});
